import React, { useState, useEffect } from 'react';

/**
 * Check if a URL is a Firebase Storage URL (permanent, never expires)
 */
function isFirebaseStorageUrl(url) {
  if (!url) return false;
  return (url.includes('storage.googleapis.com') && url.includes('Signature=')) ||
         url.includes('firebasestorage.googleapis.com');
}

/**
 * PlacePhotoPicker - Allows selecting a photo from places in the list
 * Shows a grid of place photos that the user can select from.
 *
 * Only displays photos that are already cached in Firebase Storage from enrichment.
 * Does NOT trigger any new Google API calls.
 */
function PlacePhotoPicker({ places, onSelectPhoto, onClose }) {
  const [placePhotos, setPlacePhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!places || places.length === 0) {
      setLoading(false);
      return;
    }

    // Only show places that already have Firebase Storage photo URLs from enrichment
    // This avoids triggering any Google API calls
    const placesWithPhotos = places
      .filter(place => place.photoUrl && isFirebaseStorageUrl(place.photoUrl))
      .map(place => ({
        placeId: place.placeId,
        name: place.name || place.title,
        photoUrl: place.photoUrl,
      }));

    setPlacePhotos(placesWithPhotos);
    setLoading(false);
  }, [places]);

  const handleSelectPhoto = (photoUrl) => {
    onSelectPhoto(photoUrl);
    onClose();
  };

  if (loading) {
    return (
      <>
        <div className="place-photo-picker-backdrop" onClick={onClose} />
        <div className="place-photo-picker">
          <div className="place-photo-picker-header">
            <h3>Choose from Places</h3>
            <button type="button" className="place-photo-picker-close" onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="place-photo-picker-loading">
            <p>Loading photos...</p>
          </div>
        </div>
      </>
    );
  }

  if (placePhotos.length === 0) {
    return (
      <>
        <div className="place-photo-picker-backdrop" onClick={onClose} />
        <div className="place-photo-picker">
          <div className="place-photo-picker-header">
            <h3>Choose from Places</h3>
            <button type="button" className="place-photo-picker-close" onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="place-photo-picker-empty">
            <p>No photos available from places in this list.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="place-photo-picker-backdrop" onClick={onClose} />
      <div className="place-photo-picker">
        <div className="place-photo-picker-header">
          <h3>Choose from Places</h3>
          <button type="button" className="place-photo-picker-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="place-photo-picker-grid">
          {placePhotos.map((place) => (
            <button
              key={place.placeId}
              type="button"
              className="place-photo-picker-item"
              onClick={() => handleSelectPhoto(place.photoUrl)}
            >
              <img src={place.photoUrl} alt={place.name} />
              <span className="place-photo-picker-name">{place.name}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

export default PlacePhotoPicker;
